import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api } from '@/utils/api';
import type { Shift } from '@/types';

/** Pedido que bloquea el cierre de turno (viene en el 409 de /close). */
export interface BlockingOrder {
  id: string;
  tranid: string;
  status: string;
  customer_name: string | null;
  priority: string;
}

export const useShiftStore = defineStore('shift', () => {
  const current = ref<Shift | null>(null);
  const loading = ref(false);
  // Pedidos abiertos que impiden fichar salida (el supervisor tiene que liberarlos).
  const blockingOrders = ref<BlockingOrder[]>([]);
  // Tick para que el reloj del turno avance en pantalla.
  const nowTick = ref(Date.now());
  setInterval(() => (nowTick.value = Date.now()), 1000);

  const isOpen = computed(() => current.value !== null);

  /** Segundos fichados hasta ahora (turno abierto). */
  const elapsedSec = computed(() => {
    if (!current.value) return 0;
    return Math.max(0, Math.floor((nowTick.value - new Date(current.value.started_at).getTime()) / 1000));
  });

  /** "2h 30m" / "45m" — para la barra de turno. */
  const elapsedLabel = computed(() => {
    const s = elapsedSec.value;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
  });

  async function open(warehouseId?: string): Promise<Shift> {
    loading.value = true;
    try {
      const { data } = await api.post<{ shift: Shift; reused: boolean }>('/v1/shifts/open', {
        warehouse_id: warehouseId,
      });
      current.value = data.shift;
      return data.shift;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Ficha salida. Devuelve true si cerró; si hay pedidos abiertos devuelve false y
   * deja la lista en blockingOrders para que la vista la muestre (no se puede cerrar
   * el turno con pedidos a medio armar: el supervisor los libera o transfiere primero).
   */
  async function close(): Promise<boolean> {
    if (!current.value) return true;
    blockingOrders.value = [];
    loading.value = true;
    try {
      await api.post(`/v1/shifts/${current.value.id}/close`);
      current.value = null;
      return true;
    } catch (e: any) {
      // El interceptor de axios (utils/api.ts) normaliza a { status, code, message, details }
      // PLANO — el dato vive en e.details, no en e.response.data.
      const blocked = e?.details?.open_orders;
      if (Array.isArray(blocked)) {
        blockingOrders.value = blocked;
        return false;
      }
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function restore(): Promise<void> {
    try {
      const { data } = await api.get<{ shift: Shift | null }>('/v1/shifts/current');
      current.value = data.shift;
    } catch {
      current.value = null;
    }
  }

  return {
    current, loading, blockingOrders, isOpen, elapsedSec, elapsedLabel,
    open, close, restore,
  };
});
